import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import OptionModal from '../../../modals/option-modal';
import { useService } from '../../../../services/services';
import { useSelector, useStore } from 'react-redux';
import { RootState } from '../../../../store/store';
import checkBoxCss from './../../../../components/form/elements/checkbox.module.css';
import css from './table-configuration-modal.module.css';
import { FormProvider, useForm } from 'react-hook-form';
import { user } from '../../../../store/global-slice';
import { TableColumnConfigurationProps } from '../use-column';

interface Choices {
  [key: string]: boolean;
}

export default function TableConfigurationModal({
  config,
  setShowTableConfigurationModal,
  minimumValues = 3,
}: {
  config: string;
  setShowTableConfigurationModal: Dispatch<SetStateAction<boolean>>;
  minimumValues?: number;
}) {
  const [columnConfiguration, setColumnConfiguration] = useState<
    TableColumnConfigurationProps[]
  >([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const { t } = useTranslation();
  const { userService, authService } = useService();
  const { dispatch } = useStore();

  const { register, setValue, getValues, ...formMethods } = useForm({
    resolver: null,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { userState } = useSelector((state: RootState) => ({
    userState: state.global.user,
  }));

  useEffect(() => {
    const subscription = userService
      .tableConfigurations(config)
      .subscribe((values: TableColumnConfigurationProps[]) => {
        setColumnConfiguration(values);
        if (userState?.table_column_configuration?.[config]) {
          userState?.table_column_configuration?.[config]?.forEach((e) => {
            setValue(e.id, true);
          });

          return;
        }

        values.forEach((e) => {
          if (e.is_default) {
            setValue(e.id, true);
          }
        });
      });

    return () => subscription.unsubscribe();
  }, [config, setValue, userService, userState?.table_column_configuration]);

  const submit = useCallback(
    (form: Choices) => {
      userService
        .tableConfigurationsUpdate(config, { config: form })
        .subscribe(() => {
          setShowTableConfigurationModal(false);
          authService.user().subscribe((userResponse) => {
            dispatch(user(userResponse)); // Refresh stored user
          });
        });
    },
    [authService, config, dispatch, setShowTableConfigurationModal, userService]
  );

  return (
    <OptionModal
      title={undefined}
      className={css['config-modal']}
      content={
        <FormProvider
          setValue={setValue}
          register={register}
          getValues={getValues}
          {...formMethods}
        >
          <form>
            <div className={css['config-list']}>
              {columnConfiguration.map((e) => (
                <div className={css['config-item']}>
                  <span className={css['config-label']}>
                    {t(e.translation_key)}
                  </span>
                  <div className={css['config-checkbox']}>
                    <input
                      type="checkbox"
                      id={e.id}
                      name={e.id}
                      className={checkBoxCss['checkbox']}
                      {...register(e.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
            {showWarning && (
              <span className={css['warning']}>
                {t('pages.crop.fields_and_works.fields.warning')}
              </span>
            )}
          </form>
        </FormProvider>
      }
      buttons={[
        { title: t('save'), name: 'yes', value: true },
        { title: t('cancel'), name: 'no', value: false },
      ]}
      onChoice={(choice) => {
        if (choice) {
          const trueCount = Object.values(getValues()).filter(Boolean).length;

          if (trueCount < minimumValues) {
            setShowWarning(true);
            return;
          }

          submit(getValues() as Choices);
          return;
        }

        setShowTableConfigurationModal(false);
      }}
      onClose={() => setShowTableConfigurationModal(false)}
    />
  );
}
